<template>
  <Teleport to="body">
    <div class="fm-root" @click.self="$emit('close')">
      <div class="fm-box" role="dialog" aria-modal="true">
        <div class="fm-head">
          <span class="fm-title">{{ t('fields.manage') }}</span>
          <button class="fm-x" :aria-label="t('drawer.close')" @click="$emit('close')">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div class="fm-body">
          <!-- Create / edit -->
          <div class="fm-section-label">{{ editingId ? t('fields.editField') : t('fields.newField') }}</div>
          <div class="fm-create">
            <input v-model="form.label" class="form-control" :placeholder="t('fields.namePlaceholder')" maxlength="80" />
            <select v-model="form.type" class="form-control fm-type">
              <option value="text">{{ t('fields.typeText') }}</option>
              <option value="number">{{ t('fields.typeNumber') }}</option>
              <option value="date">{{ t('fields.typeDate') }}</option>
              <option value="select">{{ t('fields.typeSelect') }}</option>
            </select>
          </div>

          <!-- Base colour (used on text/number/date cells, and as the fallback for
               select cells whose option doesn't carry its own colour) -->
          <div class="fm-color-row">
            <span class="fm-color-label">{{ t('fields.color') }}</span>
            <ColorSwatchPicker v-model="form.color" />
          </div>
          <p class="fm-color-hint">{{ t('fields.colorHint') }}</p>

          <div v-if="form.type === 'select'" class="fm-options">
            <div v-for="(o, i) in form.options" :key="i" class="fm-opt-row">
              <span class="fm-opt" :style="pillStyle(o.color || form.color)">{{ o.value }}</span>
              <ColorSwatchPicker v-model="o.color" compact :title="t('fields.optionColor')" />
              <button class="fm-opt-x" :title="t('fields.remove')" @click="form.options.splice(i, 1)">×</button>
            </div>
            <div class="fm-opt-add">
              <input v-model="newOption" class="form-control" :placeholder="t('fields.addValue')" @keyup.enter="addOption" />
              <button class="btn btn-ghost btn-sm" :disabled="!newOption.trim()" @click="addOption">+ {{ t('fields.addValueShort') }}</button>
            </div>
          </div>

          <p v-if="!editingId && atCap" class="fm-cap-note">{{ t('fields.maxReached') }}</p>
          <!-- Edit mode: save / cancel. Create mode: scope buttons. -->
          <div v-if="editingId" class="fm-actions">
            <button class="btn btn-ghost btn-sm" @click="cancelEdit" :disabled="busy">{{ t('common.cancel') }}</button>
            <button class="btn btn-primary btn-sm" @click="save" :disabled="!canCreate || busy">{{ t('common.saveChanges') }}</button>
          </div>
          <div v-else class="fm-actions">
            <button class="btn btn-ghost btn-sm" :class="{ active: form.scope === 'all' }" @click="create('all')" :disabled="!canCreate || busy || atCap">
              {{ t('fields.scopeAll') }}
            </button>
            <button class="btn btn-primary btn-sm" @click="create('project')" :disabled="!canCreate || busy || atCap">
              {{ t('fields.scopeThis') }}
            </button>
          </div>

          <!-- Existing -->
          <div class="fm-section-label fm-existing-label">{{ t('fields.existing') }}</div>
          <p v-if="!fields.length" class="fm-empty">{{ t('fields.empty') }}</p>
          <ul v-else class="fm-list">
            <li v-for="f in fields" :key="f.id" class="fm-item" :class="{ editing: editingId === f.id }" :style="{ '--field-tint': f.color || 'transparent' }">
              <span v-if="f.color" class="fm-item-swatch" :style="{ background: f.color }"></span>
              <span class="fm-item-name">{{ f.label }}</span>
              <span class="fm-item-type">{{ t('fields.type' + cap(f.type)) }}</span>
              <span v-if="f.project_id === null" class="fm-item-badge">{{ t('fields.globalBadge') }}</span>
              <button class="fm-item-edit" :title="t('common.edit')" @click="startEdit(f)">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              </button>
              <ColorSwatchPicker :model-value="f.color" compact @update:modelValue="v => setFieldColor(f, v)" :title="t('fields.color')" />
              <button class="fm-item-x" :title="t('fields.remove')" @click="remove(f)">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { reactive, ref, computed } from 'vue'
import api from '@/api'
import { useI18n } from '@/composables/useI18n'
import { useToast } from '@/composables/useToast'
import ColorSwatchPicker from '@/components/ColorSwatchPicker.vue'

// Contrast ink so text stays readable across the whole palette (light bgs get dark
// text, dark bgs get white). Keeps the pill preview honest.
function inkFor(hex) {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex || '')) return 'inherit'
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  const yiq = (r * 299 + g * 587 + b * 114) / 1000
  return yiq > 175 ? '#0F1420' : '#FFFFFF'
}

export default {
  name: 'FieldManagerModal',
  components: { ColorSwatchPicker },
  props: {
    projectId:   { type: Number, required: true },
    projectName: { type: String, default: '' },
    fields:      { type: Array,  default: () => [] },
  },
  emits: ['close', 'changed'],
  setup(props, { emit }) {
    const { t } = useI18n()
    const toast = useToast()
    const busy  = ref(false)
    const newOption = ref('')
    // Options are held as {value, color}. The API accepts both this shape and legacy
    // strings, so old fields keep working before the migration is applied everywhere.
    const form = reactive({ label: '', type: 'text', options: [], color: null, scope: 'project' })
    // Id of the field currently being edited (null = create mode).
    const editingId = ref(null)

    const MAX_FIELDS = 4
    const atCap = computed(() => props.fields.length >= MAX_FIELDS)

    const canCreate = computed(() =>
      form.label.trim() && (form.type !== 'select' || form.options.length > 0))

    const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1)

    function addOption() {
      const v = newOption.value.trim()
      if (v && !form.options.some(o => o.value === v)) form.options.push({ value: v, color: null })
      newOption.value = ''
    }

    function pillStyle(hex) {
      if (!hex) return {}
      return { background: hex, color: inkFor(hex) }
    }

    function resetForm() {
      Object.assign(form, { label: '', type: 'text', options: [], color: null, scope: 'project' })
      newOption.value = ''
    }

    async function create(scope) {
      if (!canCreate.value || busy.value) return
      busy.value = true
      try {
        await api.createProjectField(props.projectId, {
          label: form.label.trim(),
          type: form.type,
          options: form.type === 'select' ? form.options : undefined,
          color: form.color || null,
          scope,
        })
        resetForm()
        toast.success(t('toast.saved'))
        emit('changed')
      } catch { toast.error(t('toast.saveFailed')) }
      finally { busy.value = false }
    }

    // Load an existing field into the form for editing. Options are cloned so
    // in-form edits don't mutate the prop until saved.
    function startEdit(f) {
      editingId.value = f.id
      Object.assign(form, {
        label: f.label || '',
        type: f.type || 'text',
        options: (f.options || []).map(o => ({ value: o.value, color: o.color || null })),
        color: f.color || null,
        scope: 'project',
      })
      newOption.value = ''
    }

    function cancelEdit() {
      editingId.value = null
      resetForm()
    }

    async function save() {
      if (!canCreate.value || busy.value || !editingId.value) return
      busy.value = true
      try {
        await api.updateProjectField(editingId.value, {
          label: form.label.trim(),
          type: form.type,
          options: form.type === 'select' ? form.options : [],
          color: form.color || null,
        })
        editingId.value = null
        resetForm()
        toast.success(t('toast.saved'))
        emit('changed')
      } catch { toast.error(t('toast.saveFailed')) }
      finally { busy.value = false }
    }

    // Change an existing field's colour inline — no need to reopen an editor.
    async function setFieldColor(f, color) {
      try {
        await api.updateProjectField(f.id, { color: color || null })
        emit('changed')
      } catch { toast.error(t('toast.saveFailed')) }
    }

    async function remove(f) {
      if (!confirm(t('fields.removeConfirm', { label: f.label }))) return
      try { await api.deleteProjectField(f.id); toast.success(t('toast.saved')); emit('changed') }
      catch { toast.error(t('toast.actionFailed')) }
    }

    return { t, busy, newOption, form, editingId, canCreate, atCap, cap, addOption, create, remove, pillStyle, setFieldColor, startEdit, cancelEdit, save }
  },
}
</script>

<style scoped>
.fm-root { position: fixed; inset: 0; z-index: 1200; display: grid; place-items: center; background: rgba(16,22,40,.42); padding: 20px; }
.fm-box { width: min(500px, 100%); max-height: 86vh; overflow-y: auto; background: var(--surface); border: 1px solid var(--border); border-radius: 16px; box-shadow: 0 18px 48px rgba(16,22,40,.28); }
.fm-head { display: flex; align-items: center; justify-content: space-between; padding: 16px 18px; border-bottom: 1px solid var(--border); }
.fm-title { font-size: 16px; font-weight: 700; }
.fm-x { border: none; background: none; cursor: pointer; color: var(--muted); display: inline-flex; padding: 4px; border-radius: 8px; }
.fm-x:hover { color: var(--text); background: var(--hover); }
.fm-body { padding: 16px 18px; }
.fm-section-label { font-size: 12px; font-weight: 700; color: var(--muted); text-transform: uppercase; letter-spacing: .03em; margin-bottom: 10px; }
.fm-existing-label { margin-top: 22px; padding-top: 16px; border-top: 1px solid var(--border); }
.fm-create { display: flex; gap: 8px; }
.fm-type { width: 130px; flex-shrink: 0; }

/* Base colour row */
.fm-color-row { display: flex; align-items: center; gap: 10px; margin-top: 10px; }
.fm-color-label { font-size: 12.5px; font-weight: 600; color: var(--muted); }
.fm-color-hint { font-size: 11.5px; color: var(--faint); margin-top: 4px; }

/* Select options — each row: preview pill + option colour picker + remove */
.fm-options { display: flex; flex-direction: column; gap: 6px; margin-top: 12px; }
.fm-opt-row { display: flex; align-items: center; gap: 8px; }
.fm-opt {
  flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  border-radius: 999px; padding: 5px 12px; font-size: 13px; font-weight: 600;
  background: var(--accent-wash); color: var(--accent-ink);
}
.fm-opt-x { border: none; background: none; cursor: pointer; color: var(--muted); font-size: 18px; line-height: 1; padding: 0 4px; }
.fm-opt-x:hover { color: var(--danger); }
.fm-opt-add { display: flex; gap: 6px; margin-top: 4px; }
.fm-opt-add .form-control { flex: 1; }
.fm-cap-note { font-size: 12.5px; color: var(--warning); margin-top: 12px; }
.fm-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 12px; }
.btn-ghost.active { border-color: var(--accent); color: var(--accent-ink); }
.fm-empty { font-size: 13px; color: var(--faint); }
.fm-list { list-style: none; display: flex; flex-direction: column; gap: 6px; }
.fm-item { display: flex; align-items: center; gap: 10px; padding: 8px 10px; background: var(--surface-2); border: 1px solid var(--border); border-radius: 10px; }
.fm-item-swatch { width: 14px; height: 14px; border-radius: 4px; flex-shrink: 0; box-shadow: 0 0 0 1px rgba(0,0,0,.08); }
.fm-item-name { font-size: 14px; font-weight: 600; flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; }
.fm-item-type { font-size: 11px; color: var(--muted); }
.fm-item-badge { font-size: 10px; font-weight: 700; color: var(--accent-ink); background: var(--accent-wash); border-radius: 6px; padding: 1px 6px; }
.fm-item-edit { border: none; background: none; cursor: pointer; color: var(--muted); display: inline-flex; padding: 3px; border-radius: 6px; }
.fm-item-edit:hover { color: var(--accent-ink); background: var(--accent-wash); }
.fm-item.editing { border-color: var(--accent); box-shadow: inset 0 0 0 1px var(--accent); }
.fm-item-x { border: none; background: none; cursor: pointer; color: var(--muted); display: inline-flex; padding: 3px; border-radius: 6px; }
.fm-item-x:hover { color: #fff; background: var(--danger); }
</style>
